import { useCallback, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { TagWithMetadata } from 'app/api/tag-queries';
import { usePermissions } from 'app/utils/hooks/document-permission';
import { useCreateAccessLog } from 'app/api/useAccessLogs';
import { useEditorSelector, EditorContext } from 'app/state/contexts/EditorContext';
import { resolveGoToTagAction, findTagDomNode, scrollToNode, blinkTagNode, GoToTagAction } from 'app/utils/tag-utils';
import { i18n } from 'app/utils/i18n';

const MAX_RESOLVE_FRAMES = 10;

export interface GoToTagState {
  kind: GoToTagAction['kind'];
  disabled: boolean;
  tooltip?: string;
}

export const useGoToTag = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const { sectionPermissions } = usePermissions(documentId);
  const permissions = useMemo(() => (sectionPermissions ?? {}) as Record<string, unknown>, [sectionPermissions]);
  const { mutate: createAccessLog } = useCreateAccessLog();
  const section = useEditorSelector((editor: any) => editor.section);
  const pDocument = useEditorSelector((editor: any) => editor.pDocument);
  const ctx = useContext(EditorContext) as { shadowHost?: HTMLElement & { shadowRoot: ShadowRoot | null } } | null;
  const shadowHost = ctx?.shadowHost;

  const currentSectionId = section?.superId ?? null;

  const scrollAndBlink = useCallback(
    (tag: Pick<TagWithMetadata, 'id' | 'onId'>) => {
      const root: ParentNode | null = shadowHost?.shadowRoot ?? (typeof document !== 'undefined' ? document : null);
      if (!root) return;
      let frames = 0;
      const attempt = () => {
        const node = findTagDomNode(root, { id: tag.id, onId: tag.onId });
        if (node) {
          scrollToNode(node);
          blinkTagNode(node);
          return;
        }
        if (frames++ < MAX_RESOLVE_FRAMES) requestAnimationFrame(attempt);
      };
      attempt();
    },
    [shadowHost]
  );

  const goToTag = useCallback(
    (tag: TagWithMetadata) => {
      const action = resolveGoToTagAction(tag, currentSectionId, permissions);
      if (action.kind === 'blocked') return;
      if (action.kind === 'scroll') {
        scrollAndBlink(tag);
        return;
      }
      //@ts-ignore create access log hook is not typed
      createAccessLog({
        sectionSuperId: tag.sectionSuperId,
        documentSuperId: pDocument?.superId,
        organizationId: pDocument?.organization,
      });
      // Cross-section jump needs a full reload; stash the tag in sessionStorage so the blink survives it.
      window.sessionStorage.setItem('goToTag', JSON.stringify({ id: tag.id, onId: tag.onId ?? null }));
      const hash = tag.pageId ? `#${tag.pageId}` : '';
      window.location.href = `/client/documents/${documentId}/sections/${tag.sectionSuperId}?taggingMode=true${hash}`;
    },
    [currentSectionId, permissions, scrollAndBlink, createAccessLog, pDocument, documentId]
  );

  const getGoToTagState = useCallback(
    (tag: TagWithMetadata): GoToTagState => {
      const action = resolveGoToTagAction(tag, currentSectionId, permissions);
      if (action.kind === 'blocked') {
        return {
          kind: action.kind,
          disabled: true,
          tooltip: action.reason === 'hidden' ? i18n('tag.go.to.tag.hidden') : i18n('tag.go.to.tag.no.access'),
        };
      }
      return { kind: action.kind, disabled: false };
    },
    [currentSectionId, permissions]
  );

  return { goToTag, getGoToTagState };
};
